import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  DownloadCloud,
  Check,
  X,
  Eye,
  ScrollText,
  RefreshCw,
  FileText,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const CATEGORIES = [
  "دستوري",
  "مدني",
  "جزائي",
  "تجاري",
  "أحوال شخصية",
  "عمل",
  "إجراءات",
  "إداري",
  "مالي",
  "أخرى",
];

function fmtBytes(n) {
  if (!n || n <= 0) return "0 MB";
  const mb = n / 1048576;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

function fmtDuration(secs, isAr) {
  if (secs == null || secs < 0) return isAr ? "يُحتسب…" : "calculating…";
  const s = Math.round(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return isAr ? `${h} س ${m} د` : `${h}h ${m}m`;
  if (m > 0) return isAr ? `${m} د ${sec} ث` : `${m}m ${sec}s`;
  return isAr ? `${sec} ث` : `${sec}s`;
}

export default function AdminLawQueue() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [active, setActive] = useState(null); // full queue item being previewed
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [ingestStatus, setIngestStatus] = useState(null);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    let alive = true;
    const poll = () => api.get("/admin/ingest-status")
      .then((r) => alive && setIngestStatus(r.data))
      .catch(() => {});
    poll();
    const iv = setInterval(poll, 8000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  const resumeIngest = async () => {
    setResuming(true);
    try {
      const r = await api.post("/admin/ingest-resume");
      toast.success(isAr ? "تم استئناف المعالجة" : "Ingestion resumed");
      setIngestStatus((s) => ({ ...(s || {}), running: true }));
    } catch {
      toast.error(isAr ? "تعذّر الاستئناف" : "Failed to resume");
    } finally {
      setResuming(false);
    }
  };

  const t = useMemo(
    () => ({
      eyebrow: isAr ? "استيراد رسمي" : "Official ingestion",
      title: isAr ? "طابور مراجعة القوانين" : "Law review queue",
      lead: isAr
        ? "ألصق رابط ملف PDF لقانون رسمي (وزارة العدل مثلاً). يقوم النظام باستخراج النص بدقة عبر OCR ويضعه هنا للمراجعة قبل نشره في المكتبة."
        : "Paste an official law PDF URL. The system OCR-extracts clean Arabic text and stages it here for review before publishing.",
      ingestTitle: isAr ? "استيراد قانون من رابط رسمي" : "Ingest a law from an official URL",
      urlLabel: isAr ? "رابط PDF الرسمي" : "Official PDF URL",
      titleLabel: isAr ? "عنوان القانون (اختياري)" : "Law title (optional)",
      catLabel: isAr ? "التصنيف (اختياري)" : "Category (optional)",
      ingestBtn: isAr ? "بدء الاستخلاص (OCR)" : "Start ingestion (OCR)",
      ingestStarted: isAr
        ? "بدأ الاستخلاص. سيظهر القانون في الطابور خلال دقائق حسب حجم الملف."
        : "Ingestion started. The law will appear in the queue in a few minutes.",
      pending: isAr ? "بانتظار المراجعة" : "Pending review",
      empty: isAr ? "لا توجد قوانين قيد المراجعة حالياً." : "No laws pending review.",
      refresh: isAr ? "تحديث" : "Refresh",
      approveAll: isAr ? "اعتماد الكل ونشره" : "Approve & publish all",
      confirmApproveAll: (n) =>
        isAr ? `اعتماد ونشر ${n} قانوناً دفعة واحدة في المكتبة؟` : `Approve & publish all ${n} laws?`,
      approvedAll: (n) => (isAr ? `تم نشر ${n} قانوناً في المكتبة ✔` : `Published ${n} laws ✔`),
      preview: isAr ? "معاينة" : "Preview",
      approve: isAr ? "اعتماد ونشر" : "Approve & publish",
      reject: isAr ? "رفض" : "Reject",
      confirmReject: isAr ? "رفض هذا القانون وحذفه من الطابور؟" : "Reject and remove this law from the queue?",
      approved: isAr ? "تم النشر في المكتبة ✔" : "Published to the library ✔",
      rejected: isAr ? "تم الرفض" : "Rejected",
      save: isAr ? "حفظ التعديلات" : "Save changes",
      saved: isAr ? "تم الحفظ" : "Saved",
      body: isAr ? "نص القانون" : "Law body",
      memo: isAr ? "المذكرة التفسيرية" : "Explanatory memorandum",
      noMemo: isAr ? "لا توجد مذكرة تفسيرية." : "No explanatory memorandum.",
      close: isAr ? "إغلاق" : "Close",
      hasMemo: isAr ? "بمذكرة تفسيرية" : "Has memorandum",
      chars: (n) => (isAr ? `${(n || 0).toLocaleString("ar")} حرف` : `${(n || 0).toLocaleString()} chars`),
      err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong.",
      checkUpdates: isAr ? "فحص التحديثات الرسمية الآن" : "Check official updates now",
      updateStarted: isAr
        ? "بدأ فحص القوانين الجديدة من موقع وزارة العدل — ستظهر الجديدة في الطابور خلال دقائق."
        : "Checking for new official laws…",
      weeklyNote: isAr
        ? "تحديث تلقائي أسبوعي (الأحد صباحاً بتوقيت الكويت) يجلب القوانين الجديدة إلى هذا الطابور للمراجعة."
        : "Auto weekly (Sun morning, Kuwait time) pulls new laws into this queue.",
      lastRun: isAr ? "آخر فحص" : "Last check",
      foundNew: (n) =>
        isAr ? `${n} قانوناً جديداً بانتظار المراجعة` : `${n} new pending review`,
      importNote: isAr
        ? "استيراد دفعة القوانين المجهّزة (128 قانوناً) إلى قاعدة بيانات هذا الموقع مباشرةً — بدون OCR وبدون المساس ببيانات المستخدمين. استخدمها بعد النشر لإظهار كل القوانين على الموقع المباشر."
        : "Import the prepared laws bundle (128 laws) straight into this site's database — no OCR, no user-data changes. Use after deploy to show all laws on the live site.",
      importBtn: isAr ? "استيراد دفعة القوانين (128)" : "Import laws bundle (128)",
      importDoneMsg: (d) =>
        isAr
          ? `تم الاستيراد ✔ — جديد: ${d.inserted}، محدّث: ${d.updated}، الإجمالي المنشور: ${d.total_public}`
          : `Imported ✔ — new: ${d.inserted}, updated: ${d.updated}, total public: ${d.total_public}`,
    }),
    [isAr]
  );

  const load = useCallback(() => {
    setLoadingList(true);
    api
      .get("/admin/laws/queue")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/laws/update-status").then((r) => setUpdateStatus(r.data || {})).catch(() => {});
    }
  }, [user]);

  const onCheckUpdates = async () => {
    setCheckingUpdates(true);
    try {
      await api.post("/admin/laws/check-updates");
      toast.success(t.updateStarted);
      setTimeout(async () => {
        try {
          const r = await api.get("/admin/laws/update-status");
          setUpdateStatus(r.data || {});
        } catch {}
        load();
      }, 9000);
    } catch {
      toast.error(t.err);
    } finally {
      setCheckingUpdates(false);
    }
  };

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onIngest = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setIngesting(true);
    try {
      await api.post("/admin/laws/queue/ingest", {
        url: url.trim(),
        title: title.trim() || undefined,
        category: category || undefined,
      });
      toast.success(t.ingestStarted);
      setUrl("");
      setTitle("");
      setCategory("");
      setTimeout(load, 4000);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t.err);
    } finally {
      setIngesting(false);
    }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/laws/queue/${id}`);
      setActive(data);
    } catch {
      toast.error(t.err);
    }
  };

  const onSave = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.patch(`/admin/laws/queue/${active.id}`, {
        title: active.title,
        text: active.text,
        explanatory_memorandum: active.explanatory_memorandum,
        category: active.category,
      });
      toast.success(t.saved);
      load();
    } catch {
      toast.error(t.err);
    } finally {
      setSaving(false);
    }
  };

  const onApprove = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/laws/queue/${id}/approve`);
      toast.success(t.approved);
      setActive(null);
      load();
    } catch {
      toast.error(t.err);
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (id) => {
    if (!window.confirm(t.confirmReject)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/laws/queue/${id}/reject`);
      toast.success(t.rejected);
      setActive(null);
      load();
    } catch {
      toast.error(t.err);
    } finally {
      setBusyId(null);
    }
  };

  const onApproveAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(t.confirmApproveAll(items.length))) return;
    setApprovingAll(true);
    try {
      const { data } = await api.post("/admin/laws/queue/approve-all");
      toast.success(t.approvedAll(data?.approved ?? 0));
      setActive(null);
      load();
    } catch {
      toast.error(t.err);
    } finally {
      setApprovingAll(false);
    }
  };

  const onImportBundle = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/laws/import-bundle", null, { timeout: 120000 });
      toast.success(t.importDoneMsg(data || {}));
    } catch {
      toast.error(t.err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="admin-law-queue-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      {/* OCR INGESTION PROGRESS */}
      {ingestStatus && (
        <div
          data-testid="ingest-status-panel"
          className="mb-10 rounded-sm border p-6"
          style={{ background: "var(--navy)", borderColor: "var(--navy)" }}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" style={{ color: "var(--gold)" }} />
              <h2 className="font-display text-lg text-white">
                {isAr ? "حالة معالجة القوانين (OCR)" : "Law OCR ingestion status"}
              </h2>
              <span
                data-testid="ingest-status-badge"
                className="text-[11px] px-2.5 py-0.5 rounded-full"
                style={{
                  background: ingestStatus.running ? "rgba(52,211,153,0.2)" : "rgba(255,255,255,0.12)",
                  color: ingestStatus.running ? "#34d399" : "rgba(255,255,255,0.7)",
                }}
              >
                {ingestStatus.running
                  ? (isAr ? "قيد المعالجة" : "Running")
                  : ingestStatus.finalizing
                    ? (isAr ? "جارِ الاعتماد" : "Finalizing")
                    : (isAr ? "متوقفة" : "Idle")}
              </span>
            </div>
            {!ingestStatus.running && (
              <Button
                type="button"
                onClick={resumeIngest}
                disabled={resuming}
                data-testid="ingest-resume-btn"
                className="h-9 text-xs"
                style={{ background: "var(--gold-deep)", color: "#fff" }}
              >
                {resuming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span className="ms-2">{isAr ? "استئناف المعالجة" : "Resume ingestion"}</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {[
              [isAr ? "ملفات اكتملت" : "Files done", `${ingestStatus.files_done} / ${ingestStatus.files_total}`],
              [isAr ? "متبقٍّ" : "Remaining", ingestStatus.files_remaining],
              [isAr ? "قوانين بالمكتبة" : "Public laws", ingestStatus.public_laws],
              [isAr ? "بانتظار الاعتماد" : "Pending queue", ingestStatus.queue_pending],
            ].map(([label, val], i) => (
              <div key={i} className="rounded-sm bg-white/5 p-3" data-testid={`ingest-stat-${i}`}>
                <p className="text-[11px] text-white/50 mb-1">{label}</p>
                <p className="font-display text-xl text-white">{val}</p>
              </div>
            ))}
          </div>

          {/* progress bar (by processed volume when available) */}
          {(ingestStatus.bytes_total > 0 || ingestStatus.files_total > 0) && (
            <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-2">
              <div
                data-testid="ingest-progress-bar"
                className="h-full transition-all"
                style={{
                  width: `${ingestStatus.bytes_total > 0
                    ? ingestStatus.bytes_percent
                    : Math.round((ingestStatus.files_done / ingestStatus.files_total) * 100)}%`,
                  background: "var(--gold)",
                }}
              />
            </div>
          )}

          {/* processed volume + estimated time remaining */}
          {ingestStatus.bytes_total > 0 && (
            <div
              data-testid="ingest-size-eta"
              className="flex items-center justify-between gap-3 flex-wrap text-xs mb-3"
            >
              <span className="text-white/70" data-testid="ingest-size">
                {isAr ? "الحجم المُعالَج: " : "Processed volume: "}
                <span className="text-white font-medium">
                  {fmtBytes(ingestStatus.bytes_done)} / {fmtBytes(ingestStatus.bytes_total)}
                </span>
                <span className="text-[var(--gold)]"> ({ingestStatus.bytes_percent}%)</span>
              </span>
              {(ingestStatus.running || ingestStatus.finalizing) && (
                <span className="text-white/70" data-testid="ingest-eta">
                  {isAr ? "الوقت المتبقّي المقدّر: " : "Est. time remaining: "}
                  <span className="text-white font-medium">
                    {fmtDuration(ingestStatus.eta_seconds, isAr)}
                  </span>
                  {ingestStatus.elapsed_seconds != null && (
                    <span className="text-white/40">
                      {" "}· {isAr ? "منقضٍ " : "elapsed "}{fmtDuration(ingestStatus.elapsed_seconds, isAr)}
                    </span>
                  )}
                </span>
              )}
            </div>
          )}

          {ingestStatus.running && ingestStatus.current_file && (
            <p className="text-sm text-white/70" data-testid="ingest-current">
              {isAr ? "الملف الحالي: " : "Current file: "}
              <span className="text-white">{ingestStatus.current_file}</span>
              {ingestStatus.current_total_pages ? (
                <span className="text-[var(--gold)]"> — {isAr ? "صفحة" : "page"} {ingestStatus.current_page}/{ingestStatus.current_total_pages}</span>
              ) : null}
            </p>
          )}
          {ingestStatus.bundle_updated_at && (
            <p className="text-xs text-white/40 mt-2" data-testid="ingest-bundle">
              {isAr ? "آخر تحديث للحزمة: " : "Bundle updated: "}
              {new Date(ingestStatus.bundle_updated_at).toLocaleString(isAr ? "ar" : "en")}
              {ingestStatus.bundle_count ? ` (${ingestStatus.bundle_count} ${isAr ? "قانون" : "laws"})` : ""}
            </p>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* INGEST FORM */}
      <form
        onSubmit={onIngest}
        data-testid="law-ingest-form"
        className="border border-amber-300 bg-amber-50 p-5 lg:p-6 mb-10 grid md:grid-cols-2 gap-4 items-end"
      >
        <div className="md:col-span-2 flex items-center gap-2">
          <DownloadCloud className="w-4 h-4 text-amber-700" />
          <p className="eyebrow text-amber-900">{t.ingestTitle}</p>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.urlLabel}</Label>
          <Input
            type="url"
            dir="ltr"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.moj.gov.kw/AR/Documents/MojDocs/..."
            data-testid="law-ingest-url"
            className="mt-2 rounded-sm h-11 bg-white"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.titleLabel}</Label>
          <Input
            dir="auto"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="law-ingest-title"
            className="mt-2 rounded-sm h-11 bg-white"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.catLabel}</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="law-ingest-category"
            className="mt-2 rounded-sm h-11 w-full border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="">—</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={ingesting || !url.trim()}
            data-testid="law-ingest-submit"
            className="rounded-sm h-12 bg-amber-700 hover:bg-amber-800 text-white"
          >
            {ingesting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.ingestBtn}
          </Button>
        </div>
      </form>

      {/* WEEKLY UPDATE BAR */}
      <div
        data-testid="law-updates-bar"
        className="flex flex-col md:flex-row md:items-center justify-between gap-3 border border-slate-200 bg-white p-4 mb-8 rounded-sm"
      >
        <div className="text-sm text-slate-600">
          <p className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-slate-400" />
            {t.weeklyNote}
          </p>
          {updateStatus?.last_run && (
            <p className="text-xs text-slate-400 mt-1" data-testid="law-update-status">
              {t.lastRun}: {new Date(updateStatus.last_run).toLocaleString(isAr ? "ar" : "en")}
              {" · "}
              {t.foundNew(updateStatus.pending_new ?? updateStatus.new_total ?? 0)}
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={onCheckUpdates}
          disabled={checkingUpdates}
          data-testid="law-check-updates"
          className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white shrink-0"
        >
          {checkingUpdates ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <span className="inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              {t.checkUpdates}
            </span>
          )}
        </Button>
      </div>

      {/* IMPORT BUNDLE BAR — seed prod DB with all laws after deploy */}
      <div
        data-testid="law-import-bundle-bar"
        className="flex flex-col md:flex-row md:items-center justify-between gap-3 border border-emerald-300 bg-emerald-50 p-4 mb-8 rounded-sm"
      >
        <p className="text-sm text-emerald-900 flex items-start gap-2">
          <DownloadCloud className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" />
          {t.importNote}
        </p>
        <Button
          type="button"
          onClick={onImportBundle}
          disabled={importing}
          data-testid="law-import-bundle"
          className="rounded-sm h-11 bg-emerald-700 hover:bg-emerald-800 text-white shrink-0"
        >
          {importing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <span className="inline-flex items-center gap-2">
              <DownloadCloud className="w-4 h-4" />
              {t.importBtn}
            </span>
          )}
        </Button>
      </div>

      {/* QUEUE LIST */}
      <div className="flex items-center justify-between mb-4">
        <p className="eyebrow flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-slate-500" />
          {t.pending} {items.length > 0 && <span className="text-slate-400">· {items.length}</span>}
        </p>
        <div className="flex items-center gap-4">
          {items.length > 0 && (
            <button
              type="button"
              onClick={onApproveAll}
              disabled={approvingAll}
              data-testid="law-queue-approve-all"
              className="inline-flex items-center gap-2 h-9 px-4 text-sm rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-60"
            >
              {approvingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {t.approveAll} ({items.length})
            </button>
          )}
          <button
            type="button"
            onClick={load}
            data-testid="law-queue-refresh"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          >
            <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
            {t.refresh}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white">
        {loadingList ? (
          <div className="p-10 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="law-queue-empty">
            {t.empty}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((q) => (
              <li
                key={q.id}
                data-testid={`law-queue-row-${q.id}`}
                className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-5"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate" dir="rtl">
                    {q.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span>{t.chars(q.char_count)}</span>
                    {q.category ? <span>· {q.category}</span> : null}
                    {q.law_number && q.law_year ? (
                      <span>
                        · رقم {q.law_number} لسنة {q.law_year}
                      </span>
                    ) : null}
                    {q.has_memo ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <FileText className="w-3 h-3" /> {t.hasMemo}
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openPreview(q.id)}
                    data-testid={`law-queue-preview-${q.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-sm border border-slate-300 hover:bg-slate-50"
                  >
                    <Eye className="w-4 h-4" /> {t.preview}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === q.id}
                    onClick={() => onApprove(q.id)}
                    data-testid={`law-queue-approve-${q.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {busyId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {t.approve}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === q.id}
                    onClick={() => onReject(q.id)}
                    data-testid={`law-queue-reject-${q.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50"
                  >
                    <X className="w-4 h-4" /> {t.reject}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* PREVIEW / EDIT OVERLAY */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="law-queue-preview-overlay"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-white w-full max-w-4xl max-h-[90vh] overflow-auto rounded-sm p-6 lg:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">{t.preview}</p>
              <button
                type="button"
                onClick={() => setActive(null)}
                data-testid="law-queue-close-preview"
                className="text-slate-400 hover:text-slate-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.titleLabel}</Label>
            <Input
              dir="rtl"
              value={active.title || ""}
              onChange={(e) => setActive({ ...active, title: e.target.value })}
              data-testid="law-queue-edit-title"
              className="mt-2 mb-4 rounded-sm h-11"
            />

            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.body}</Label>
            <textarea
              dir="rtl"
              value={active.text || ""}
              onChange={(e) => setActive({ ...active, text: e.target.value })}
              data-testid="law-queue-edit-text"
              className="mt-2 mb-4 w-full h-64 border border-slate-300 rounded-sm p-3 text-sm leading-7"
              style={{ fontFamily: "Tajawal, sans-serif" }}
            />

            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.memo}</Label>
            {active.explanatory_memorandum ? (
              <textarea
                dir="rtl"
                value={active.explanatory_memorandum || ""}
                onChange={(e) => setActive({ ...active, explanatory_memorandum: e.target.value })}
                data-testid="law-queue-edit-memo"
                className="mt-2 mb-4 w-full h-48 border border-slate-300 rounded-sm p-3 text-sm leading-7"
                style={{ fontFamily: "Tajawal, sans-serif" }}
              />
            ) : (
              <p className="mt-2 mb-4 text-sm text-slate-400">{t.noMemo}</p>
            )}

            <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-100">
              <Button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="law-queue-save"
                className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.save}
              </Button>
              <Button
                type="button"
                onClick={() => onApprove(active.id)}
                disabled={busyId === active.id}
                data-testid="law-queue-approve-modal"
                className="rounded-sm h-11 bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                {busyId === active.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="w-4 h-4" /> {t.approve}
                  </span>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onReject(active.id)}
                data-testid="law-queue-reject-modal"
                className="rounded-sm h-11 border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <span className="inline-flex items-center gap-1.5">
                  <X className="w-4 h-4" /> {t.reject}
                </span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
